import type { SettingsDirectoryEntry } from '@/config/settings-directory'
import type { SettingTerm } from '@/lib/settings-search/types'
import type { ComponentType } from 'react'
import { useMemo } from 'react'
import {
  MODULE_SETTINGS_PATHS,
  SETTINGS_DIRECTORY,
} from '@/config/settings-directory'
import { SETTINGS_SEARCH_INDEX } from '@/config/settings-search-index.generated'
import {
  ADMIN_SETTINGS_SECTIONS,
  MODULE_SETTINGS_SECTIONS,
  USER_SETTINGS_TABS,
} from '@/config/settings-sections'

import { useVisibleNavItems } from './useVisibleNavItems'

export interface ResolvedSettingsSection {
  id: string
  label: string
  keywords: readonly string[]
  /** Org-relative deep link; null when not navigable for this user */
  path: string | null
  /** Personal preference row inside an org group (never badged read-only) */
  personal: boolean
  /** Settings rendered in this section, from the generated search index. */
  settings?: readonly SettingTerm[]
  /** On search results: the setting this section matched through, if any. */
  matchedSetting?: SettingTerm
}

export interface ResolvedSettingsGroup {
  entry: SettingsDirectoryEntry
  /**
   * Settings not tied to a listed section: a single-pane page's settings, or
   * an admin page's content that has no sub-page row of its own.
   */
  settings?: readonly SettingTerm[]
  /** On search results: the group-level setting this group matched through. */
  matchedSetting?: SettingTerm
  label: string
  icon?: ComponentType<{ className?: string }>
  /** Org-relative link for the whole group; null when not navigable */
  path: string | null
  canEdit: boolean
  sections: ResolvedSettingsSection[]
}

export interface SettingsDirectoryResult {
  personal: ResolvedSettingsGroup[]
  org: ResolvedSettingsGroup[]
  admin: ResolvedSettingsGroup[]
  /** True until permissions resolve — render a skeleton, not read-only badges */
  isLoading: boolean
}

/**
 * Resolves SETTINGS_DIRECTORY against the current user's account type,
 * feature flags, subscription, and permissions. Module-level gates hide
 * entries entirely (matching the sidebar). Module settings require their
 * manage permission; administration requires the organization's Owner/Admin
 * role. Personal settings have no additional permission gate.
 */
export function useSettingsDirectory(): SettingsDirectoryResult {
  const {
    visibleMainNavItems,
    adminNavItems,
    userMenuItems,
    isAdminOrOwner,
    isPersonal,
    checkFeatureFlag,
    accountType,
    checkPermission,
    isLoadingPermissions,
  } = useVisibleNavItems()

  return useMemo(() => {
    const navByHref = new Map(
      visibleMainNavItems.map(item => [item.href, item]),
    )
    const adminByHref = new Map(adminNavItems.map(item => [item.href, item]))
    const userMenuByHref = new Map(userMenuItems.map(item => [item.href, item]))
    const isOrgAdmin = isAdminOrOwner

    const personal: ResolvedSettingsGroup[] = []
    const org: ResolvedSettingsGroup[] = []
    const admin: ResolvedSettingsGroup[] = []

    for (const entry of SETTINGS_DIRECTORY) {
      if (entry.accountTypes && !entry.accountTypes.includes(accountType)) {
        continue
      }

      const navItem =
        entry.scope === 'admin'
          ? adminByHref.get(entry.navHref ?? '')
          : entry.navHref
            ? navByHref.get(entry.navHref)
            : undefined
      if (entry.navHref && !navItem) continue
      if (
        navItem?.accountTypes &&
        !navItem.accountTypes.includes(accountType)
      ) {
        continue
      }
      if (entry.scope === 'admin' && !isAdminOrOwner) continue

      const canEdit = !entry.editGate
        ? true
        : entry.editGate.kind === 'orgAdmin'
          ? isOrgAdmin
          : checkPermission(entry.editGate.module, entry.editGate.action)
      if (entry.scope !== 'personal' && (!canEdit || isLoadingPermissions)) {
        continue
      }

      const settingsPath =
        entry.settingsPath ??
        (entry.navHref ? MODULE_SETTINGS_PATHS[entry.navHref] : undefined)
      const groupPath = canEdit && settingsPath ? settingsPath : null

      let sections: ResolvedSettingsSection[] = []
      if (entry.id === 'personal:profile' && settingsPath) {
        sections = USER_SETTINGS_TABS.map(tab => ({
          id: tab.id,
          label: tab.label,
          keywords: tab.keywords ?? [],
          path: `${settingsPath}#${tab.id}`,
          personal: true,
          settings: SETTINGS_SEARCH_INDEX.pages[entry.id]?.[tab.id],
        }))
      } else if (entry.id === 'admin:/admin/settings' && settingsPath) {
        sections = ADMIN_SETTINGS_SECTIONS.filter(
          section =>
            !(section.businessOnly && isPersonal) &&
            !(section.personalOnly && !isPersonal) &&
            (!section.featureFlag || checkFeatureFlag?.(section.featureFlag)) &&
            (!section.orgAdmin || isAdminOrOwner),
        ).map(section => ({
          id: section.id,
          label: section.label,
          keywords: section.keywords ?? [],
          path: groupPath ? `${settingsPath}?section=${section.id}` : null,
          personal: false,
          settings: SETTINGS_SEARCH_INDEX.pages[entry.id]?.[section.id],
        }))
      } else if (entry.adminSections && settingsPath) {
        // Admin sub-pages: rows only the user can open (the page itself is
        // owner/admin gated), each hosted at its own shell route.
        sections = entry.adminSections.map(section => ({
          id: section.id,
          label: section.label,
          keywords: section.keywords ?? [],
          path: groupPath
            ? section.subPage
              ? `${settingsPath}/${section.subPage}`
              : settingsPath
            : null,
          personal: false,
          settings:
            SETTINGS_SEARCH_INDEX.pages[entry.id]?.[
              section.subPage ?? section.id
            ],
        }))
      } else if (entry.navHref && entry.scope !== 'admin' && settingsPath) {
        const sectionConfig = MODULE_SETTINGS_SECTIONS[entry.navHref]
        if (sectionConfig) {
          const param = sectionConfig.param ?? 'section'
          const editGate = entry.editGate
          const indexedSections = SETTINGS_SEARCH_INDEX.modules[entry.navHref]
          sections = sectionConfig.sections
            .filter(section => {
              if (section.businessOnly && isPersonal) return false
              if (section.orgAdmin && !(isOrgAdmin && !isPersonal)) return false
              // A section-level gate is an extra action on the entry's module
              // (projects' Notifications needs PROJECT.APPROVE); the page
              // hides the section with the same check.
              if (section.gate && editGate?.kind === 'module') {
                return checkPermission(editGate.module, section.gate)
              }
              return true
            })
            .map(section => {
              const isPersonalSection =
                entry.scope === 'personal' || section.personal === true
              // Personal entries are available without a manage permission.
              const navigable = Boolean(groupPath) || isPersonalSection
              return {
                id: section.id,
                label: section.label,
                keywords: section.keywords ?? [],
                path: navigable
                  ? `${settingsPath}?${param}=${section.id}`
                  : null,
                personal: isPersonalSection,
                settings: indexedSections?.[section.id],
              }
            })
        }
      }

      // Settings the index holds for this page but no listed row covers: a
      // single-pane page's settings, or admin content without a sub-page row.
      const indexedPage =
        entry.navHref && entry.scope !== 'admin'
          ? SETTINGS_SEARCH_INDEX.modules[entry.navHref]
          : SETTINGS_SEARCH_INDEX.pages[entry.id]
      const listedSectionIds = new Set(sections.map(section => section.id))
      for (const section of entry.adminSections ?? []) {
        if (section.subPage) listedSectionIds.add(section.subPage)
      }
      const groupSettings = indexedPage
        ? Object.entries(indexedPage)
            .filter(([id]) => !listedSectionIds.has(id))
            .flatMap(([, terms]) => terms)
        : []

      const group: ResolvedSettingsGroup = {
        entry,
        // Admin entries prefer their own label ("Organization Settings" over
        // the admin nav's bare "Settings"); module entries prefer the NavItem
        // label so terminology overrides (Clients, Vendors) carry through.
        label:
          entry.scope === 'admin'
            ? (entry.label ?? navItem?.label ?? entry.id)
            : (navItem?.label ?? entry.label ?? entry.id),
        icon:
          navItem?.icon ??
          (entry.id === 'personal:profile'
            ? userMenuByHref.get('/user-settings')?.icon
            : entry.id === 'personal:account'
              ? userMenuByHref.get('/account')?.icon
              : undefined),
        path: groupPath,
        canEdit,
        sections,
        settings: groupSettings.length ? groupSettings : undefined,
      }

      if (entry.scope === 'personal') personal.push(group)
      else if (entry.scope === 'org') org.push(group)
      else admin.push(group)
    }

    return { personal, org, admin, isLoading: isLoadingPermissions }
  }, [
    accountType,
    adminNavItems,
    checkPermission,
    isAdminOrOwner,
    isLoadingPermissions,
    isPersonal,
    checkFeatureFlag,
    userMenuItems,
    visibleMainNavItems,
  ])
}
